// third-party
import { createSlice } from '@reduxjs/toolkit';
import { forEach, isEqual } from 'lodash';

// project imports
import { dispatch } from '../index';

import { getVitalsDataMeta, getVitalsData, getVitalsDevices } from 'api/vitalsData';
import { VITALS_DATA_TYPES, VITALS_RANGE_PRESETS } from 'utils/vitalsData';

// ----------------------------------------------------------------------

const emptyTypeState = () => ({
    loaded: false,
    loading: false,
    error: false,
    data: [],
    summary: null,
    range: null,
    devices: [],
    referenceLines: {},
    history: { latestReadingTime: null, latestReadingDate: null, allTimeReadings: 0 }
});

const initialState = {
    meta: {
        loaded: false,
        error: false,
        data: {
            BLOOD_GLUCOSE: false,
            BLOOD_PRESSURE: false,
            PULSE_OXIMETER: false,
            WEIGHT: false
        }
    },
    selectedVitalsDataType: null,
    // what the user picked; the server resolves it to dates in the app zone
    selectedRange: {
        preset: VITALS_RANGE_PRESETS.LAST_30_DAYS,
        from: null,
        to: null
    },
    vitalsData: {
        BLOOD_GLUCOSE: emptyTypeState(),
        BLOOD_PRESSURE: emptyTypeState(),
        PULSE_OXIMETER: emptyTypeState(),
        WEIGHT: emptyTypeState()
    },
    vitalsDevices: {
        loaded: false,
        error: false,
        data: []
    }
};

const slice = createSlice({
    name: 'vitalsData',
    initialState,
    reducers: {
        setVitalsMeta(state, action) {
            state.meta.loaded = true;
            state.meta.error = false;
            state.meta.data = action.payload;
        },
        setVitalsMetaError(state, action) {
            state.meta.loaded = true;
            state.meta.error = action.payload;
        },
        setVitalsDataLoadingForType(state, action) {
            state.vitalsData[action.payload.type].loading = true;
        },
        setVitalsDataForType(state, action) {
            const { type, selectedRange, vitalsData, summary, range, devices, referenceLines, history } = action.payload;
            if (!isEqual(selectedRange, state.selectedRange)) {
                // a response for a range the user has since moved away from
                return;
            }
            state.vitalsData[type] = {
                loaded: true,
                loading: false,
                error: false,
                data: vitalsData,
                summary,
                range,
                devices,
                referenceLines,
                history
            };
        },
        setVitalsDataErrorForType(state, action) {
            const { type, selectedRange, error } = action.payload;
            if (!isEqual(selectedRange, state.selectedRange)) {
                return;
            }
            state.vitalsData[type].loaded = true;
            state.vitalsData[type].loading = false;
            state.vitalsData[type].error = error;
        },
        setSelectedVitalsType(state, action) {
            state.selectedVitalsDataType = action.payload;
        },
        // a new range invalidates every type: each refetches when it is next shown,
        // and nothing from the old range is left to show if that refetch fails
        setSelectedRange(state, action) {
            state.selectedRange = action.payload;
            forEach(VITALS_DATA_TYPES, (type) => {
                state.vitalsData[type] = emptyTypeState();
            });
        },

        setVitalsDevices(state, action) {
            state.vitalsDevices.loaded = true;
            state.vitalsDevices.error = false;
            state.vitalsDevices.data = action.payload;
        },
        setVitalsDevicesError(state, action) {
            state.vitalsDevices.loaded = true;
            state.vitalsDevices.error = action.payload;
        },

        resetState(state) {
            state.meta = initialState.meta;
            state.vitalsData = initialState.vitalsData;
            state.selectedVitalsDataType = initialState.selectedVitalsDataType;
            state.selectedRange = initialState.selectedRange;
            state.vitalsDevices = initialState.vitalsDevices;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function resetState() {
    return dispatch(slice.actions.resetState());
}

export function getMeta(patientId) {
    return async () => {
        const response = await getVitalsDataMeta(patientId);
        if (response.status === 200) {
            dispatch(slice.actions.setVitalsMeta(response.data.meta));
        } else {
            dispatch(slice.actions.setVitalsMetaError(response.message));
        }
    };
}

export function getVitalsDataForType(patientId, type, selectedRange) {
    return async () => {
        dispatch(slice.actions.setVitalsDataLoadingForType({ type }));
        const response = await getVitalsData(patientId, type, selectedRange);
        if (response.status === 200) {
            dispatch(slice.actions.setVitalsDataForType({ type, selectedRange, ...response.data }));
        } else {
            dispatch(slice.actions.setVitalsDataErrorForType({ type, selectedRange, error: response.message }));
        }
    };
}

export function setSelectedVitalsType(type) {
    dispatch(slice.actions.setSelectedVitalsType(type));
}

export function setSelectedRange(selectedRange) {
    dispatch(slice.actions.setSelectedRange(selectedRange));
}

export function getVitalsDevicesList(patientId) {
    return async () => {
        const response = await getVitalsDevices(patientId);
        if (response.status === 200) {
            dispatch(slice.actions.setVitalsDevices(response.data.devices));
        } else {
            dispatch(slice.actions.setVitalsDevicesError(response.message));
        }
    };
}
